import { css } from '@emotion/react';
import React, { ReactNode } from 'react';

import {
  colors,
  fonts,
  fontSizes,
  fontWeights,
  lineHeights,
  space,
} from '../shared/styles';

export interface FormFieldProps {
  /**
   * Field label
   */
  label?: string;
  /**
   * Whether to hide the label visually
   */
  hideLabel?: boolean;
  /**
   * Whether the field is required
   */
  required?: boolean;
  /**
   * Error message
   */
  error?: string;
  /**
   * Success message
   */
  success?: string;
  /**
   * Help text
   */
  helpText?: string;
  /**
   * Field ID for accessibility
   */
  htmlFor?: string;
  /**
   * Children components
   */
  children: ReactNode;
  /**
   * Additional CSS class names
   */
  className?: string;
}

const fieldContainerStyles = css`
  display: flex;
  flex-direction: column;
  gap: ${space['2']};
`;

const labelStyles = css`
  font-family: ${fonts.base};
  font-size: ${fontSizes.sm};
  font-weight: ${fontWeights.medium};
  line-height: ${lineHeights.tight};
  color: ${colors.gray['900']};
  margin-bottom: ${space['1']};
`;

const requiredIndicatorStyles = css`
  color: ${colors.red['500']};
  margin-left: ${space['1']};
`;

const helpTextStyles = css`
  font-family: ${fonts.base};
  font-size: ${fontSizes.sm};
  line-height: ${lineHeights.tight};
  color: ${colors.gray['600']};
`;

const errorTextStyles = css`
  font-family: ${fonts.base};
  font-size: ${fontSizes.sm};
  line-height: ${lineHeights.tight};
  color: ${colors.red['500']};
  display: flex;
  align-items: center;
  gap: ${space['1']};
`;

const successTextStyles = css`
  font-family: ${fonts.base};
  font-size: ${fontSizes.sm};
  line-height: ${lineHeights.tight};
  color: ${colors.accent.green};
  display: flex;
  align-items: center;
  gap: ${space['1']};
`;

const visuallyHiddenStyles = css`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const FormField: React.FC<FormFieldProps> = ({
  label,
  hideLabel = false,
  required = false,
  error,
  success,
  helpText,
  htmlFor,
  children,
  className = '',
}) => {
  const hasError = !!error;
  const hasSuccess = !!success && !hasError;
  const hasHelpText = !!helpText && !hasError && !hasSuccess;

  return (
    <div css={fieldContainerStyles} className={className}>
      {label && (
        <label
          htmlFor={htmlFor}
          css={[labelStyles, hideLabel && visuallyHiddenStyles]}
        >
          {label}
          {required && (
            <span css={requiredIndicatorStyles} aria-label="required">
              *
            </span>
          )}
        </label>
      )}

      {children}

      {hasError && (
        <div css={errorTextStyles} role="alert">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zM8 4a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 8 4zm0 8a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"
              clipRule="evenodd"
            />
          </svg>
          {error}
        </div>
      )}

      {hasSuccess && (
        <div css={successTextStyles}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm3.844-8.791a.75.75 0 0 0-1.188-.918l-3.7 4.79-1.649-1.833a.75.75 0 1 0-1.114 1.004l2.25 2.5a.75.75 0 0 0 1.15-.043l4.25-5.5z"
              clipRule="evenodd"
            />
          </svg>
          {success}
        </div>
      )}

      {hasHelpText && <div css={helpTextStyles}>{helpText}</div>}
    </div>
  );
};
